import { FC } from "react";
import { CalendarBlankLined, MapMarkerFilled } from "@tag/tag-icons";
import { Pill, Pills, Popover } from "@tag/tag-components-react-v4";
import { useTheme } from "../../../../../../context/themeContext";
import "./weekDayPublicHolidays.css";
import { useForecastContext } from "../../../../../../context/forecastContext";
import { publicHolidaysForDate } from "../../../../../../utilities/publicHolidayHelpers";
import dayjs from "dayjs";
import SkeletonLoader from "../../../../../SkeletonLoader";
import ErrorMessage from "../../../../../errorMessage/errorMessage";
import { usePublicHolidayQuery } from "../../../../../../services/nationalHolidayService";

type WeekDayPublicHolidaysProps = {
  day: dayjs.Dayjs;
};

const WeekDayPublicHolidays: FC<WeekDayPublicHolidaysProps> = ({ day }) => {
  const { theme } = useTheme();
  const { forecastState } = useForecastContext();
  const {
    data: publicHolidays,
    isLoading,
    isError,
  } = usePublicHolidayQuery({
    searchPeriod: forecastState.searchPeriod,
    country: "GB",
  });

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (isError) {
    return <ErrorMessage dataType="Public Holidays" fontSize="11px" />;
  }

  const publicHols = publicHolidaysForDate(publicHolidays ?? [], day);

  return (
    <div className="week-day-public-holidays-container">
      {publicHols.length > 0 ? (
        publicHols.map((holiday, index) => (
          <div className="week-day-public-holiday-info-item" key={index}>
            <span>
              <CalendarBlankLined accent="teal" /> {holiday.localName}
            </span>
            <Popover
              popoverFor={
                <div className="week-day-public-holiday-regions">
                  <MapMarkerFilled accent="teal" />
                </div>
              }
              background={theme}
              style={{
                borderRadius: "8px",
                border: "1px solid var(--border-color)",
              }}
            >
              <div className="week-day-public-holidays-region-popover-content">
                <div className="week-day-public-holidays-region-popover-header">
                  Regions
                </div>
                <div className="week-day-public-holidays-region-popover-description">
                  {`${holiday.localName} is a public holiday in the following regions:`}
                </div>

                <div className="week-day-public-holidays-region-popover-pills">
                  {holiday.subDivionCodes &&
                  holiday.subDivionCodes.length > 0 ? (
                    <Pills>
                      {holiday.subDivisionNames?.map((item, index) => (
                        <Pill key={index}>{item}</Pill>
                      ))}
                    </Pills>
                  ) : (
                    <Pills>
                      <Pill>{holiday.countryName}</Pill>
                    </Pills>
                  )}
                </div>
              </div>
            </Popover>
          </div>
        ))
      ) : (
        <div className="week-day-no-public-holidays-container">
          <span>
            <CalendarBlankLined accent="teal" /> {"No public holidays"}
          </span>
        </div>
      )}
    </div>
  );
};

export default WeekDayPublicHolidays;
